import { Suspense, lazy, type ComponentType } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Loading } from '../components/Ui';
import { Layout } from '../components/Layout';
import { RequireUser } from '../components/RequireUser';
import { Login, Register } from '../pages/auth/Auth';
import { Device } from '../pages/auth/Device';
import { People } from '../pages/public/People';
import { Explore } from '../pages/public/Explore';
import { Home } from '../pages/workspace/Home';
import { NewRepo } from '../pages/workspace/NewRepo';
import { NotFound } from '../pages/workspace/NotFound';
import { Notifications } from '../pages/workspace/Notifications';
import { Profile } from '../pages/workspace/Profile';
import { RepoBlob, RepoHome, RepoTree } from '../pages/wiki/RepoFiles';
import { RepoCommit, RepoHistory } from '../pages/wiki/RepoHistory';
import { RepoLayout } from '../pages/wiki/RepoLayout';
import { YourWikis, StarredWikis } from '../pages/workspace/WorkspaceWikis';
import { Guide } from '../pages/public/Guide';
import { RepoForks } from '../pages/wiki/RepoForks';

/**
 * Conversations (issues, proposals, discussions) load on first use, so the front page, discovery and
 * reading pages do not pay for their editor, timeline and review code.
 */
const ConversationList = lazy(() =>
  import('../pages/collaboration/Conversations').then((m) => ({
    default: m.ConversationList,
  })),
);
const NewConversation = lazy(() =>
  import('../pages/collaboration/NewConversation').then((m) => ({
    default: m.NewConversation,
  })),
);
const NewProposal = lazy(() =>
  import('../pages/collaboration/NewConversation').then((m) => ({
    default: m.NewProposal,
  })),
);
const ThreadLayout = lazy(() =>
  import('../pages/collaboration/Thread').then((m) => ({
    default: m.ThreadLayout,
  })),
);
const ConversationTab = lazy(() =>
  import('../pages/collaboration/ThreadConversation').then((m) => ({
    default: m.ConversationTab,
  })),
);
const ProposalFiles = lazy(() =>
  import('../pages/collaboration/ProposalFiles').then((m) => ({
    default: m.ProposalFiles,
  })),
);
const UserSettings = lazy(() =>
  import('../pages/account/UserSettings').then((m) => ({
    default: m.UserSettings,
  })),
);
const RepoSettings = lazy(() =>
  import('../pages/wiki/RepoSettings').then((m) => ({
    default: m.RepoSettings,
  })),
);
const Preferences = lazy(() =>
  import('../pages/account/Preferences').then((m) => ({
    default: m.Preferences,
  })),
);
const Download = lazy(() => import('../pages/public/Download').then((m) => ({ default: m.Download })));

/** Renders a lazily loaded page; its props are checked against the page's own prop types. */
function Later<P extends object>({ page: Page, ...props }: { page: ComponentType<P> } & P) {
  return (
    <Suspense fallback={<Loading />}>
      <Page {...(props as unknown as P)} />
    </Suspense>
  );
}

export function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="explore" element={<Explore />} />
        <Route path="explore/people" element={<People />} />
        <Route path="settings/following" element={<RequireUser>{() => <People following />}</RequireUser>} />
        <Route path="download" element={<Later page={Download} />} />
        <Route path="explore/guide" element={<Guide />} />
        <Route path="explore/preferences" element={<Later page={Preferences} />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="new" element={<NewRepo />} />
        <Route path="settings" element={<Later page={UserSettings} />} />
        <Route path="settings/account" element={<Later page={UserSettings} />} />
        <Route path="settings/profile" element={<Later page={UserSettings} />} />
<Route path="settings/privacy" element={<Later page={UserSettings} />} />
<Route path="settings/password" element={<Later page={UserSettings} />} />
<Route path="settings/deactivate" element={<Later page={UserSettings} />} />
<Route path="settings/two-factor" element={<Later page={UserSettings} />} />
        <Route path="settings/security" element={<Later page={UserSettings} />} />
        <Route path="settings/tokens" element={<Later page={UserSettings} />} />
        <Route path="settings/wikis" element={<RequireUser>{() => <YourWikis />}</RequireUser>} />
        <Route path="settings/starred" element={<RequireUser>{() => <StarredWikis />}</RequireUser>} />
        <Route
          path="settings/appearance"
          element={<RequireUser>{() => <Later page={Preferences} />}</RequireUser>}
        />
        <Route
          path="settings/accessibility"
          element={<RequireUser>{() => <Later page={Preferences} />}</RequireUser>}
        />
        <Route path="notifications" element={<RequireUser>{() => <Notifications />}</RequireUser>} />
        <Route path="device" element={<Device />} />
        <Route path="user/:owner" element={<Profile />} />
        <Route path=":owner" element={<Profile />} />
        <Route path=":owner/:repo" element={<RepoLayout />}>
          <Route index element={<RepoHome />} />
          <Route path="tree/*" element={<RepoTree />} />
          <Route path="blob/*" element={<RepoBlob />} />
          <Route path="commits" element={<RepoHistory />} />
          <Route path="commit/:rev" element={<RepoCommit />} />
          <Route path="settings" element={<Later page={RepoSettings} />} />
          <Route path="forks" element={<RepoForks />} />
          <Route path="issues" element={<Later page={ConversationList} kind="issue" />} />
          <Route path="issues/new" element={<Later page={NewConversation} kind="issue" />} />
          <Route path="issues/:number" element={<Later page={ThreadLayout} kind="issue" />}>
            <Route index element={<Later page={ConversationTab} />} />
          </Route>
          <Route path="proposals" element={<Later page={ConversationList} kind="proposal" />} />
          <Route path="proposals/new" element={<Later page={NewProposal} />} />
          <Route path="proposals/:number" element={<Later page={ThreadLayout} kind="proposal" />}>
            <Route index element={<Later page={ConversationTab} />} />
            <Route path="files" element={<Later page={ProposalFiles} />} />
          </Route>
          <Route path="discussions" element={<Later page={ConversationList} kind="discussion" />} />
          <Route path="discussions/new" element={<Later page={NewConversation} kind="discussion" />} />
          <Route path="discussions/:number" element={<Later page={ThreadLayout} kind="discussion" />}>
            <Route index element={<Later page={ConversationTab} />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
